/**
 * Sync Engine — Phase 2
 *
 * Client-side orchestration layer for the bidirectional sync system.
 * This module provides:
 *
 *   1. Trust-rule evaluator  — given a change event and a connector's
 *      trust rules, determines whether to dispatch, skip, or flag conflict.
 *
 *   2. Conflict detector     — compares an inbound external event against
 *      the current local state of the entity to identify conflicts.
 *
 *   3. Dispatch trigger      — calls the /dispatch edge function to
 *      process pending change_events for a workspace.
 *
 *   4. Conflict resolver     — writes a resolution event and updates the
 *      conflicted change_event's sync_status.
 *
 *   5. Quarantine sweeper    — marks events past the SLA as quarantined.
 *
 * The actual outbound HTTP calls to external systems live in the
 * /dispatch edge function. The rules themselves (owner, direction, conflict
 * rule per connector and field) are supabase/functions/_shared/fieldSyncRules.ts,
 * the one copy `dispatch` and `apply` also read.
 */

import { getSafeSupabaseClient, getCurrentWorkspaceId } from './supabaseSync';
import {
  fieldSyncRuleFor,
  type FieldOwner,
  type StoredFieldRules,
} from '../../supabase/functions/_shared/fieldSyncRules';

// ── Constants ─────────────────────────────────────────────────

/** After this many milliseconds without resolution, a conflict is quarantined. */
export const CONFLICT_QUARANTINE_SLA_MS = 48 * 60 * 60 * 1000; // 48 hours

/** URL of the dispatch edge function (resolved at runtime). */
function getDispatchUrl(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
  return `${supabaseUrl}/functions/v1/dispatch`;
}

// ── Types ─────────────────────────────────────────────────────

export type SyncStatus = 'pending' | 'synced' | 'conflicted' | 'quarantined' | 'skipped';
export type EventType =
  | 'created' | 'updated' | 'deleted' | 'synced'
  | 'conflict_detected' | 'conflict_resolved'
  | 'rule_violated' | 'quarantined';

export type TrustDecision =
  | 'dispatch'   // priorities_ai is this field's source — write outbound
  | 'skip'       // external is this field's source — do not overwrite
  | 'conflict'   // external tried to change a field sourced in priorities_ai
  | 'negotiate'  // shared source — queue for human review
  | 'inbound';   // external is the field's source — apply inbound change to priorities_ai

/** A connector's Field Sync Rules as saved (connector_configs.trust_rules). */
export type TrustRules = StoredFieldRules;

export interface ChangeEvent {
  id: string;
  workspaceId: string;
  source: string;
  entityType: string;
  entityId: string;
  fieldName: string | null;
  oldValue: unknown;
  newValue: unknown;
  eventType: EventType;
  syncStatus: SyncStatus;
  conflictData: ConflictData | null;
  createdAt: string;
}

export interface ConflictData {
  trustDecision: TrustDecision;
  incomingSource: string;
  field: string | null;
  incomingValue: unknown;
  localValue?: unknown;
  detectedAt: string;
  resolutionOptions?: Array<{ label: string; value: 'local' | 'external' | 'manual' }>;
}

export interface ConflictResolution {
  winner: 'local' | 'external';
  resolvedValue: unknown;
  resolvedBy: string; // user ID
  note?: string;
}

export interface DispatchRequest {
  workspaceId: string;
  eventId?: string;
  mode?: 'pending' | 'retry' | 'conflicts';
}

export interface DispatchResponse {
  processed: number;
  dispatched: number;
  conflicts: number;
  skipped: number;
  failed: number;
  results: Array<{
    eventId: string;
    connectorType: string;
    decision: string;
    status: string;
  }>;
}

// ── 1. Trust-rule evaluator ───────────────────────────────────

/** Which side owns a field under a connector's Field Sync Rules (the item page reads the same answer). */
export function ownerOfField(trustRules: TrustRules, fieldName: string): FieldOwner {
  return fieldSyncRuleFor(trustRules, fieldName).owner;
}

// ── 2. Conflict detector ──────────────────────────────────────

/**
 * Build conflict data for an event where an external system tried to
 * change a field sourced in priorities_ai.
 */
export function buildConflictData(
  event: ChangeEvent,
  localValue?: unknown,
): ConflictData {
  return {
    trustDecision: 'conflict',
    incomingSource: event.source,
    field: event.fieldName,
    incomingValue: event.newValue,
    localValue,
    detectedAt: new Date().toISOString(),
    resolutionOptions: [
      { label: 'Keep priorities.ai value', value: 'local' },
      { label: `Accept ${event.source} value`, value: 'external' },
      { label: 'Resolve manually', value: 'manual' },
    ],
  };
}

/**
 * Check whether a pending event is past the quarantine SLA.
 */
export function isPastQuarantineSLA(event: ChangeEvent): boolean {
  const age = Date.now() - new Date(event.createdAt).getTime();
  return age > CONFLICT_QUARANTINE_SLA_MS;
}

// ── 3. Dispatch trigger ───────────────────────────────────────

/**
 * Trigger the /dispatch edge function to process pending events
 * for the current workspace.
 *
 * This is the primary way the app initiates outbound sync from
 * the browser. Call it after any prioritization mutation that
 * should propagate to connected systems.
 */
export async function triggerDispatch(
  params: DispatchRequest,
): Promise<{ data: DispatchResponse | null; error: string | null }> {
  try {
    const url = new URL(getDispatchUrl());
    url.searchParams.set('workspace_id', params.workspaceId);
    if (params.eventId) url.searchParams.set('event_id', params.eventId);
    if (params.mode) url.searchParams.set('mode', params.mode);

    // `dispatch` checks its caller in-function: the signed-in person, a member of the workspace.
    const { data: sessionData } = (await getSafeSupabaseClient()?.auth.getSession()) ?? { data: { session: null } };
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) return { data: null, error: 'Sign in required' };
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, ...(anonKey ? { apikey: anonKey } : {}) },
    });
    const data = await response.json() as DispatchResponse;

    if (!response.ok) {
      return { data: null, error: `Dispatch failed: HTTP ${response.status}` };
    }
    return { data, error: null };
  } catch (err) {
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Dispatch request failed',
    };
  }
}

/**
 * Convenience wrapper: trigger dispatch for the current workspace.
 */
export async function triggerDispatchForCurrentWorkspace(
  mode: DispatchRequest['mode'] = 'pending',
): Promise<{ data: DispatchResponse | null; error: string | null }> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { data: null, error: 'No workspace context' };
  return triggerDispatch({ workspaceId, mode });
}

// ── 4. Conflict resolver ──────────────────────────────────────

/**
 * Resolve a conflicted change event.
 *
 * - Inserts a 'conflict_resolved' change event as the resolution record.
 * - Updates the original event's sync_status to 'synced'.
 * - If winner === 'local', triggers a dispatch of the local value back
 *   to the external system.
 * - If winner === 'external', the caller is responsible for applying
 *   the external value to the local entity.
 */
export async function resolveConflict(
  eventId: string,
  resolution: ConflictResolution,
): Promise<{ error: string | null }> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { error: 'No workspace context' };

  try {
    const supabase = getSafeSupabaseClient();

    // Load the original event
    const { data: original, error: loadErr } = await supabase
      .from('change_events')
      .select('*')
      .eq('id', eventId)
      .single();

    if (loadErr || !original) {
      return { error: loadErr?.message ?? 'Event not found' };
    }

    // Insert resolution record
    const { error: insertErr } = await supabase.from('change_events').insert({
      workspace_id: workspaceId,
      source: 'priorities_ai',
      entity_type: original.entity_type,
      entity_id: original.entity_id,
      field_name: original.field_name,
      old_value: original.new_value,
      new_value: resolution.resolvedValue,
      event_type: 'conflict_resolved',
      sync_status: 'pending',
    });

    if (insertErr) return { error: insertErr.message };

    // Mark original as synced
    const { error: updateErr } = await supabase
      .from('change_events')
      .update({
        sync_status: 'synced',
        conflict_data: {
          ...((original.conflict_data as object) ?? {}),
          resolvedAt: new Date().toISOString(),
          resolvedBy: resolution.resolvedBy,
          winner: resolution.winner,
          note: resolution.note,
        },
      })
      .eq('id', eventId);

    if (updateErr) return { error: updateErr.message };

    // If local won, dispatch to push priorities_ai value back to external
    if (resolution.winner === 'local') {
      await triggerDispatch({ workspaceId, mode: 'pending' });
    }

    return { error: null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Resolution failed' };
  }
}

// ── 5. Quarantine sweeper ─────────────────────────────────────

/**
 * Mark all conflicted events past the SLA as quarantined.
 * Call this on a schedule or on page load.
 */
export async function sweepQuarantines(): Promise<{ quarantined: number; error: string | null }> {
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { quarantined: 0, error: 'No workspace context' };

  try {
    const supabase = getSafeSupabaseClient();
    const slaTimestamp = new Date(Date.now() - CONFLICT_QUARANTINE_SLA_MS).toISOString();

    const { data, error } = await supabase
      .from('change_events')
      .update({ sync_status: 'quarantined' })
      .eq('workspace_id', workspaceId)
      .eq('sync_status', 'conflicted')
      .lt('created_at', slaTimestamp)
      .select('id');

    if (error) return { quarantined: 0, error: error.message };
    return { quarantined: data?.length ?? 0, error: null };
  } catch (err) {
    return {
      quarantined: 0,
      error: err instanceof Error ? err.message : 'Quarantine sweep failed',
    };
  }
}

// ── 6. Pending event count helper ─────────────────────────────

/**
 * Returns counts of change_events by sync_status for a workspace.
 * Used by the SyncEventLog and ConflictQueue header stats.
 */
export async function getSyncStatusCounts(workspaceId: string): Promise<{
  pending: number;
  synced: number;
  conflicted: number;
  quarantined: number;
  skipped: number;
  error: string | null;
}> {
  try {
    const supabase = getSafeSupabaseClient();
    const { data, error } = await supabase
      .from('change_events')
      .select('sync_status')
      .eq('workspace_id', workspaceId);

    if (error) throw new Error(error.message);

    const counts = { pending: 0, synced: 0, conflicted: 0, quarantined: 0, skipped: 0 };
    for (const row of data ?? []) {
      const s = (row.sync_status as SyncStatus);
      if (s in counts) counts[s]++;
    }
    return { ...counts, error: null };
  } catch (err) {
    return {
      pending: 0, synced: 0, conflicted: 0, quarantined: 0, skipped: 0,
      error: err instanceof Error ? err.message : 'Failed to load counts',
    };
  }
}
